import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll } from 'framer-motion';
import { StarIcon } from 'lucide-react';
import { HeadingText } from '../shared/HeadingText';
import { Rail } from '../shared/Rail';
import { Section } from '../shared/Section';
import { useCatalog } from '../../contexts/CatalogContext';
import { usePages, useSite } from '../../contexts/SiteContext';
import { useLayerY } from '../../hooks/useParallax';
import { rating } from '../../core/format/currency';
import { plural } from '../../core/format/text';
import { fill } from '../../utils/copy';

/** Eén stem op de rail: een review, of een vaste cliënt met haar favoriete product. */
type Voice = {
  kind: 'review';
  quote: string;
  name: string;
  context: string;
} | {
  kind: 'client';
  quote: string;
  name: string;
  context: string;
  pickId: string;
  image: string;
};

/**
 * Reviews, na Opbouwen en vóór "één product": de score als kop, daarna alle stemmen als kaarten op de rail.
 * Geen automatisch doordraaien: je leest in je eigen tempo. Het grote aanhalingsteken ligt als
 * achterlaag achter de kaarten en beweegt trager mee. Zonder echte reviews de kop uit Paginateksten.
 */
export function HomeReviews() {
  const pages = usePages();
  const {
    shopRating,
    productById
  } = useCatalog();
  const {
    creators
  } = useSite();
  const copy = pages.home.reviews;
  // Reviews eerst, daarna vaste cliënten met hun favoriet (zonder sterren: het is geen review).
  // Een cliënt zonder bestaand product valt weg. De cliënten zelf: Instellingen › Cadeaus & codes.
  const clients: Voice[] = creators.flatMap(c => {
    const pick = productById(c.pickId);
    return pick ? [{
      kind: 'client' as const,
      quote: c.quote,
      name: c.name,
      context: `Haar favoriet: ${pick.name}`,
      pickId: pick.id,
      image: c.image
    }] : [];
  });
  const quotes: Voice[] = [...pages.testimonials.map(t => ({
    kind: 'review' as const,
    ...t
  })), ...clients];
  const count = shopRating.count;
  const avg = rating(shopRating.average);
  const ref = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start end', 'end start']
  });
  const glyphY = useLayerY(scrollYProgress, [-24, 48]);
  return <Section ref={ref} tone="shell" labelledBy="home-reviews" contained={false} data-id="element-7531" data-mp="10fa2e7w5rh0s">
      <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-7532" data-mp="10d5wjkep87xv">
        <div className="grid gap-6 lg:grid-cols-12 lg:items-end lg:gap-16" data-id="element-7533" data-mp="vhiahdvijuwl">
          <div className="lg:col-span-7" data-id="element-7534" data-mp="1t4h7xqu156vj">
            {count > 0 ? <>
                {/* De sterren zijn beeld; de kop eronder zegt hetzelfde in woorden. */}
                <p className="flex items-center gap-1" aria-hidden="true" data-id="element-7535" data-mp="1j5rty95986kk">
                  {Array.from({
                length: 5
              }, (_, i) => <StarIcon key={i} className="h-4 w-4 fill-current text-clay" strokeWidth={0} data-id="element-7536" data-mp="1e5naqbcyoxqn" />)}
                </p>
                <h2 id="home-reviews" className="mt-4 type-sub text-ink" data-id="element-7537" data-mp="7oiahmm4lej9">
                  {avg} uit {plural(count, 'beoordeling', 'beoordelingen')}
                  <span className="sr-only" data-id="element-7538" data-mp="2efmzavahpz39"> (van 5 sterren)</span>
                </h2>
              </> : <h2 id="home-reviews" className="type-sub text-ink" data-id="element-7539" data-mp="2jeq0g4ncnl5y">
                <HeadingText heading={copy.heading} data-id="element-7540" data-mp="2ocj7t10e2l7t" />
              </h2>}
          </div>
          {copy.body && <p className="max-w-md text-[17px] leading-relaxed text-muted lg:col-span-5" data-id="element-7541" data-mp="2t4t5j37qwpkn">{fill(copy.body)}</p>}
        </div>

        {quotes.length > 0 && <div className="relative mt-12 lg:mt-16" data-id="element-7542" data-mp="324fwsw6rgezz">
            <motion.span aria-hidden="true" style={glyphY ? {
          y: glyphY
        } : undefined} className="ornament-quote pointer-events-none absolute -top-14 left-0 text-clay sm:-top-20" data-id="element-7543" data-mp="3234rut1kx17a">
              “
            </motion.span>
            <Rail id="home-reviews-rail" label="Reviews" className="relative" items={quotes} itemKey={t => `${t.name}-${t.context}`} itemClassName="w-[86%] sm:w-[62%] lg:w-[calc((100%-3rem)/2.5)]" renderItem={t => <figure className="flex w-full flex-col bg-sand p-6 sm:p-8" data-id="element-7545" data-mp="335tvgqtatx1x">
                  <blockquote className="type-title leading-snug text-ink" data-id="element-7546" data-mp="3h6o4nbo6821n">{fill(t.quote)}</blockquote>
                  <figcaption className="mt-auto pt-6 text-[14px] text-muted" data-id="element-7547" data-mp="3d4je4lt0rlj8">
                    {t.kind === 'client' ?
            // Vaste cliënt: met gezicht en haar favoriet, zodat ze niet als anonieme review leest.
            <span className="flex items-center gap-3" data-id="element-7548" data-mp="2khyl7gwwb1dk">
                        {t.image && <img src={t.image} alt="" loading="lazy" width={40} height={40} className="h-10 w-10 shrink-0 rounded-full object-cover" data-id="element-7549" data-mp="2y8bt5j9seg2w" />}
                        <span data-id="element-7550" data-mp="25r7vqfis0edm">
                          <span className="block text-ink" data-id="element-7551" data-mp="25qfxsy1pi4k6">{t.name}, vaste cliënt</span>
                          <Link to={`/webshop/${t.pickId}`} className="underline-offset-4 transition-colors duration-150 ease-soft hover:text-ink hover:underline" data-id="element-7552" data-mp="21q5vpwh1byal">
                            {t.context}
                          </Link>
                        </span>
                      </span> : <>
                        {t.name} · {t.context}
                      </>}
                  </figcaption>
                </figure>} data-id="element-7544" data-mp="1cqb4oibalxj5" />
          </div>}
      </div>
    </Section>;
}